import { ReactNode, useEffect, useId, useRef } from 'react'
import { X } from 'lucide-react'

/**
 * Diálogo modal sobre <dialog> nativo: el navegador pone el foco adentro, lo atrapa
 * y cierra con Escape. El kit no trae diálogo; el aspecto vive en styles/app.css con
 * las variables de rol del kit.
 *
 * Al abrir, el foco va al control marcado con `data-autofocus` o, si no hay, al primer
 * campo del cuerpo. (`autoFocus` de React no sirve adentro: `showModal()` corre después
 * y mueve el foco al primer elemento enfocable, que es el botón de cerrar.)
 */
export function Dialogo({
  abierto, onCerrar, titulo, children, acciones, ancho = false,
}: {
  abierto: boolean
  onCerrar: () => void
  titulo: string
  children: ReactNode
  acciones?: ReactNode
  /** Más ancho (hasta 960px): para ver una foto. */
  ancho?: boolean
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const idTitulo = useId()

  useEffect(() => {
    const dialogo = ref.current
    if (!dialogo) return
    if (abierto && !dialogo.open) {
      dialogo.showModal()
      const destino =
        dialogo.querySelector<HTMLElement>('[data-autofocus]') ?? dialogo.querySelector<HTMLElement>('.dialogo__cuerpo :is(input, select, textarea)')
      destino?.focus()
    }
    if (!abierto && dialogo.open) dialogo.close()
  }, [abierto])

  return (
    <dialog ref={ref} className={`dialogo${ancho ? ' dialogo--ancho' : ''}`} aria-labelledby={idTitulo} onClose={onCerrar}>
      <header className="dialogo__cabecera">
        <h2 className="dialogo__titulo" id={idTitulo}>{titulo}</h2>
        <button type="button" className="dcc-btn dcc-btn--sm dcc-btn--text" aria-label="Cerrar" onClick={onCerrar}>
          <X className="dcc-icon dcc-i" aria-hidden />
        </button>
      </header>
      <div className="dialogo__cuerpo">{children}</div>
      {acciones && <footer className="dialogo__acciones">{acciones}</footer>}
    </dialog>
  )
}
